<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { ColorPicker } from "vue3-colorpicker";
import cloneDeep from "lodash-es/cloneDeep";
import "vue3-colorpicker/style.css";
// types
import type { ReportBlockType } from "@/common/types/report";
// components
import { baseSelect, baseSwitch, baseTextEditor } from "@/common/components";

const props = defineProps<{
  block: ReportBlockType;
  zoom: number;
}>();

const emit = defineEmits(["block:update"]);

const DEFAULT_STYLE = {
  background: "#ffffff",
  color: "#000000",
  "font-size": "16px",
  "font-style": "normal",
  "font-family": "Verdana",
  "font-weight": "normal",
  "line-height": "1.6",
  "text-align": "left",
  "text-decoration": "none",
};

const internalBlock = ref(cloneDeep(props.block));
const internalStyle = ref({ ...DEFAULT_STYLE });
const selectedTab = ref<string>("content");

const margins = ref({
  unit: "px",
  top: "0",
  right: "0",
  bottom: "0",
  left: "0",
});

const paddings = ref({
  unit: "px",
  top: "0",
  right: "0",
  bottom: "0",
  left: "0",
});

const textAlign = computed(() => internalStyle.value["text-align"]);

const optionsFonts = computed(() => [
  { option: "Arial", value: "Arial" },
  { option: "Calibri", value: "Calibri" },
  { option: "Courier", value: "Courier" },
  { option: "Georgia", value: "Georgia" },
  { option: "Open Sans", value: "Open Sans" },
  { option: "Sans Serif", value: "sans-serif" },
  { option: "Times New Roman", value: "Times New Roman" },
  { option: "Verdana", value: "Verdana" },
]);

const optionsFontSize = computed(() => [
  { option: "10px", value: "10px" },
  { option: "11px", value: "11px" },
  { option: "12px", value: "12px" },
  { option: "13px", value: "13px" },
  { option: "14px", value: "14px" },
  { option: "15px", value: "15px" },
  { option: "16px", value: "16px" },
  { option: "17px", value: "17px" },
  { option: "18px", value: "18px" },
  { option: "20px", value: "20px" },
  { option: "22px", value: "22px" },
  { option: "24px", value: "24px" },
  { option: "26px", value: "26px" },
  { option: "28px", value: "28px" },
  { option: "30px", value: "30px" },
]);

const optionsAlign = computed(() => [
  { option: "Esquerda", value: "left" },
  { option: "Centro", value: "center" },
  { option: "Direita", value: "right" },
  { option: "Justificado", value: "justify" },
]);

const optionsUnit = computed(() => [
  { option: "px", value: "px" },
  { option: "%", value: "%" },
  { option: "em", value: "em" },
  { option: "rem", value: "rem" },
]);

const tabs = computed(() => [
  {
    key: "content",
    label: "Conteúdo",
  },
  {
    key: "style",
    label: "Estilo",
  },
  {
    key: "advanced",
    label: "Avançado",
  },
]);

function formatUnit(value: string) {
  if (value?.endsWith("rem")) {
    return "rem";
  }
  if (value?.endsWith("em")) {
    return "em";
  }
  if (value?.endsWith("px")) {
    return "px";
  }
  if (value?.endsWith("%")) {
    return "%";
  }

  return "px";
}

function formatStyleToNumber(value?: string | null) {
  if (value === "" || value === undefined || value === null) {
    return "0";
  }
  return value
    ?.replace("px", "")
    ?.replace("%", "")
    ?.replace("rem", "")
    ?.replace("em", "");
}

function initialize(block: ReportBlockType) {
  internalBlock.value = {
    ...block,
    style: {
      ...DEFAULT_STYLE,
      ...block.style,
    },
  };

  internalStyle.value = {
    ...DEFAULT_STYLE,
    ...block.style,
  };

  margins.value = {
    unit: formatUnit(block.style["margin-top"]) ?? "px",
    top: formatStyleToNumber(block.style["margin-top"]),
    bottom: formatStyleToNumber(block.style["margin-bottom"]),
    right: formatStyleToNumber(block.style["margin-right"]),
    left: formatStyleToNumber(block.style["margin-left"]),
  };

  paddings.value = {
    unit: formatUnit(block.style["padding-top"]) ?? "px",
    top: formatStyleToNumber(block.style["padding-top"]),
    bottom: formatStyleToNumber(block.style["padding-bottom"]),
    right: formatStyleToNumber(block.style["padding-right"]),
    left: formatStyleToNumber(block.style["padding-left"]),
  };
}

function onUpdate(key: keyof typeof internalBlock.value, value: any) {
  internalBlock.value = {
    ...internalBlock.value,
    [key]: value,
  };

  emit("block:update", {
    block_id: internalBlock.value.block_id,
    payload: {
      [key]: value,
    },
  });
}

function changeSwitch(
  key: keyof typeof internalStyle.value,
  checked: boolean,
  value: string
) {
  const defaultValues = {
    "font-weight": "normal",
    "font-style": "normal",
    "text-align": "left",
  };

  if (!checked) {
    internalStyle.value[key] = defaultValues[key as never];
  } else {
    internalStyle.value[key] = value;
  }

  onUpdate("style", internalStyle.value);
}

function inputMargin(key: keyof typeof margins.value, event: any) {
  const value = event.target.value;

  margins.value[key] = value;

  internalStyle.value = {
    ...internalStyle.value,
    ...{
      "margin-top": `${margins.value.top}${margins.value.unit}`,
      "margin-right": `${margins.value.right}${margins.value.unit}`,
      "margin-bottom": `${margins.value.bottom}${margins.value.unit}`,
      "margin-left": `${margins.value.left}${margins.value.unit}`,
    },
  };

  onUpdate("style", internalStyle.value);
}

function inputPadding(key: keyof typeof paddings.value, event: any) {
  const value = event.target.value ?? 0;
  const sanizedValue = value.replace(/[^0-9]/g, "");

  paddings.value[key] = sanizedValue;

  internalStyle.value = {
    ...internalStyle.value,
    ...{
      "padding-top": `${paddings.value.top}${paddings.value.unit}`,
      "padding-right": `${paddings.value.right}${paddings.value.unit}`,
      "padding-bottom": `${paddings.value.bottom}${paddings.value.unit}`,
      "padding-left": `${paddings.value.left}${paddings.value.unit}`,
    },
  };

  onUpdate("style", internalStyle.value);
}

function normalizeHex(e: any) {
  let v = e.target.value.toLowerCase();

  // Força começar com #
  if (!v.startsWith("#")) v = "#" + v;

  // Aceita somente caracteres hexadecimais
  v = v.replace(/[^#0-9a-f]/g, "");

  // Máximo #RRGGBB
  if (v.length > 7) v = v.slice(0, 7);

  e.target.value = v;
  internalStyle.value.background = v;
}

watch(
  () => props.block,
  (newValue) => {
    if (newValue) {
      initialize(newValue);
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.block) {
    initialize(props.block);
  }
});
</script>

<template>
  <div class="text-editor">
    <div class="tabs">
      <template v-for="item in tabs" :key="item.key">
        <div
          class="tab"
          :class="{
            active: selectedTab === item.key,
          }"
          @click="selectedTab = item.key"
        >
          {{ item.label }}
        </div>
      </template>
    </div>

    <!-- content -->
    <template v-if="selectedTab === 'content'">
      <div class="tab-content">
        <template v-if="internalBlock">
          <base-text-editor
            v-model="internalBlock.content"
            toolbar-id="block-toolbar"
            :floating="true"
            :selected="true"
            :style="internalBlock.style"
            @update="onUpdate('content', $event)"
          />
        </template>
      </div>
    </template>

    <!-- style -->
    <template v-if="selectedTab === 'style'">
      <div class="tab-style">
        <div class="form-group">
          <label>Estilo de Fonte</label>
          <base-select
            v-model="internalStyle['font-family']"
            :value="internalStyle['font-family']"
            height="32px"
            :is-empty="false"
            :options="optionsFonts"
            @change="onUpdate('style', internalStyle)"
          />
        </div>

        <div class="form-group">
          <label>Alinhamento</label>
          <base-select
            v-model="internalStyle['text-align']"
            :value="internalStyle['text-align']"
            height="32px"
            :is-empty="false"
            :options="optionsAlign"
            @change="onUpdate('style', internalStyle)"
          />
        </div>

        <div class="form-group">
          <label>Tamanho da Fonte</label>
          <base-select
            v-model="internalStyle['font-size']"
            :value="internalStyle['font-size']"
            height="32px"
            :is-empty="false"
            :options="optionsFontSize"
            @change="onUpdate('style', internalStyle)"
          />
        </div>

        <div class="form-group">
          <label>Fromatação</label>
          <div class="switchs">
            <base-switch
              :value="internalStyle['font-weight'] === 'bold'"
              label="Negrito"
              @change="changeSwitch('font-weight', $event, 'bold')"
            />
            <base-switch
              :value="internalStyle['font-style'] === 'italic'"
              label="Itálico"
              @change="changeSwitch('font-style', $event, 'italic')"
            />
            <base-switch
              :value="internalStyle['text-decoration'] === 'underline'"
              label="Sublinhado"
              @change="changeSwitch('text-decoration', $event, 'underline')"
            />
            <base-switch
              :value="internalStyle['text-decoration'] === 'line-through'"
              label="Tachado"
              @change="changeSwitch('text-decoration', $event, 'line-through')"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Cor da Fonte</label>
          <div class="color-group">
            <input
              class="color-input"
              type="text"
              v-model="internalStyle['color']"
              maxlength="7"
              @input="normalizeHex"
              @change="onUpdate('style', internalStyle)"
              placeholder="#000000"
            />
            <ColorPicker
              v-model:pureColor="internalStyle['color']"
              format="hex"
              @update:pureColor="onUpdate('style', internalStyle)"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Cor de Fundo</label>
          <div class="color-group">
            <input
              class="color-input"
              type="text"
              v-model="internalStyle['background']"
              maxlength="7"
              @input="normalizeHex"
              @change="onUpdate('style', internalStyle)"
              placeholder="#000000"
            />
            <ColorPicker
              class="color-input"
              v-model:pureColor="internalStyle['background']"
              format="hex"
              @update:pureColor="onUpdate('style', internalStyle)"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- advanced -->
    <template v-if="selectedTab === 'advanced'">
      <div class="tab-advanced">
        <!-- margin -->
        <div class="form-group">
          <label>Margem</label>

          <div class="line-group">
            <div class="label">Unidade:</div>
            <base-select
              v-model="margins.unit"
              :value="margins.unit"
              height="30px"
              :is-empty="false"
              :options="optionsUnit"
            />
            <!-- top -->
            <div class="label">Superior:</div>
            <input
              :value="margins.top"
              type="number"
              @input="inputMargin('top', $event)"
            />
            <!-- bottom -->
            <div class="label">Inferior:</div>
            <input
              :value="margins.bottom"
              type="number"
              @input="inputMargin('bottom', $event)"
            />
            <!-- left -->
            <div class="label">Esquerda:</div>
            <input
              :value="margins.left"
              type="number"
              @input="inputMargin('left', $event)"
            />
            <!-- right -->
            <div class="label">Direita:</div>
            <input
              :value="margins.right"
              type="number"
              @input="inputMargin('right', $event)"
            />
          </div>
        </div>

        <!-- padding -->
        <div class="form-group">
          <label>Preenchimento</label>

          <div class="line-group">
            <div class="label">Unidade:</div>
            <base-select
              v-model="paddings.unit"
              :value="paddings.unit"
              height="30px"
              :is-empty="false"
              :options="optionsUnit"
            />
            <!-- top -->
            <div class="label">Superior:</div>
            <input
              :value="paddings.top"
              type="number"
              @input="inputPadding('top', $event)"
            />
            <!-- bottom -->
            <div class="label">Inferior:</div>
            <input
              :value="paddings.bottom"
              type="number"
              @input="inputPadding('bottom', $event)"
            />
            <!-- left -->
            <div class="label">Esquerda:</div>
            <input
              :value="paddings.left"
              type="number"
              @input="inputPadding('left', $event)"
            />
            <!-- right -->
            <div class="label">Direita:</div>
            <input
              :value="paddings.right"
              type="number"
              @input="inputPadding('right', $event)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.text-editor {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;

  .tabs {
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #ccc;

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      height: 40px;
      font-size: 13px;
      cursor: pointer;
      user-select: none;

      &.active {
        border-bottom: 2px solid #000;
      }
    }
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 1rem;
    overflow: auto;
    background: #fff;

    :deep(.custom-toolbar) {
      border: none !important;
      border-radius: 0px !important;
      padding: 8px !important;
      gap: 0px !important;
    }

    :deep(.ql-container.ql-snow) {
      border: none !important;
      font-size: 14px !important;
      background: #fff !important;
      margin: 0px !important;
      padding: 0px !important;
    }

    :deep(.ql-editor) {
      min-height: 300px;
      padding-inline: 8px !important;
      padding-block: 10px !important;
      padding-block-end: 50px !important;
      text-align: v-bind("textAlign") !important;
    }
  }

  .tab-style,
  .tab-advanced {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 1rem;
    overflow: auto;
    padding-inline: 15px;
    padding-block-start: 15px;
    padding-block-end: 20px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
      font-size: 0.875rem;
      font-weight: bold;
    }

    .switchs {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .color-group {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 1rem;

      .color-input {
        width: calc(100% - 20px);
        height: 30px;
        padding-inline: 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
      }
    }

    .line-group {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: center;
      gap: 0.5rem;

      .label {
        font-size: 0.875rem;
      }

      input {
        height: 32px;
        width: calc(100% - 22px);
        padding-inline: 10px;
        padding-block: 0px;
        font-size: 1rem;
        border: 1px solid #ccc;
        border-radius: 5px;
      }
    }
  }
}

:deep(.vc-color-wrap) {
  height: 30px;
  margin-right: 0px;
  padding: 1px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #ccc;
  box-shadow: none;
}

:deep(.vc-color-wrap .current-color) {
  border-radius: 4px;
}

:deep(.vc-color-wrap.transparent) {
  background-image: none;
}
</style>
